import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  CircularProgress
} from '@mui/material';
import DataProfilesSidebar from './DataProfilesSidebar';
import ProfileDashboard from './ProfileDashboard';
import DataBreadcrumb from './DataBreadcrumb';
import FilesHome from './FilesHome';

import DataProfileEditor from './DataProfileEditor';
import DeleteDataProfileDialog from './DeleteDataProfileDialog';
// Make sure onSnapshot is imported from firestore
import { collection, query, onSnapshot } from 'firebase/firestore';
import { db } from '../../../services/firebase';
import { useAttributeCategories } from '../../../contexts/AttributesContext';
import { profileName, byProfileName } from '../../../utils/profileDisplay';
import LoadFailure, { loadFailureMessage } from '../../common/LoadFailure';

// `blockedLandingBatchId` is the batch whose landing the table's claim holds
// `blocked` (pages/Data.js listens to the claim), or null: its files say so in
// place of their status, on the files list and on the profile's cards.
function DataCommandCenter({ companyId, tableId, table, blockedLandingBatchId, onBack }) {
  const [dataProfiles, setDataProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [selectedView, setSelectedView] = useState('home'); // 'home' | 'profile'
  const [searchQuery, setSearchQuery] = useState('');
  // '' is no grouping; anything else is the category to group by.
  const [groupBy, setGroupBy] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [listenerAttempt, setListenerAttempt] = useState(0);
  const [profileStats, setProfileStats] = useState({});
  // Category ids for grouping, from the live company list.
  const { categories } = useAttributeCategories();
  const attributeCategories = useMemo(() => categories.map((category) => category.id), [categories]);
  // null | { mode: 'create' } | { mode: 'edit', profile } | { mode: 'copy', profile }
  const [profileEditor, setProfileEditor] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const currentProfileRef = useRef(null);

  // THIS IS THE MODIFIED SECTION
  // Fetches initial data AND listens for real-time updates on data profiles.
  useEffect(() => {
    if (!companyId || !tableId) return;

    // Reset view when table changes
    setSelectedProfile(null);
    currentProfileRef.current = null;
    setSelectedView('home');
    setIsLoading(true);
    
    // Set up the real-time listener on the data_profiles collection
    // A failure belongs to one subscription: a new table (or Retry) starts clean,
    // and a successful payload clears it — the old page-wide error must never
    // outlive the listener that raised it.
    setError(null);
    const profilesRef = collection(db, `users/${companyId}/tables/${tableId}/data_profiles`);
    const q = query(profilesRef);

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const profiles = querySnapshot.docs.map(doc => ({
        ...doc.data(),          // spread FIRST
        id: doc.id,             // doc id always wins
      })).sort(byProfileName);

      setError(null);
      setDataProfiles(profiles);
      calculateAllProfileStats(profiles);

      // Keep the selection pointing at the live object; clear it if the doc vanished.
      setSelectedProfile(prev => {
        if (!prev) return prev;
        const fresh = profiles.find(p => p.id === prev.id);
        if (!fresh) {
          currentProfileRef.current = null;
          setSelectedView('home');
          return null;
        }
        return fresh;
      });

      setIsLoading(false); // Set loading to false after the first data payload arrives
    }, (err) => {
      console.error('Error listening to data profiles:', err);
      setError(loadFailureMessage('data profiles'));
      setIsLoading(false);
    });

    // Return the unsubscribe function to clean up the listener
    // when the component unmounts or dependencies change.
    return () => {
      unsubscribe();
    };
  }, [companyId, tableId, listenerAttempt]); // Re-run on company/table change, or Retry

  // Add event listener for create profile button
  useEffect(() => {
    const handleCreateProfile = () => {
      setProfileEditor({ mode: 'create' });
    };

    document.addEventListener('create-profile', handleCreateProfile);

    return () => {
      document.removeEventListener('create-profile', handleCreateProfile);
    };
  }, []);

  // This function remains the same, it just gets called more frequently now
  const calculateAllProfileStats = (profiles) => {
    const stats = {};
    profiles.forEach(profile => {
      const fileStats = profile.file_stats || {};
      stats[profile.id] = {
        totalFiles: fileStats.total_files || 0,
        completed: fileStats.status_completed || 0,
        needsAttention: fileStats.status_needs_attention || 0,
        processing: fileStats.status_processing || 0,
        withWarnings: fileStats.status_with_warnings || 0,
        failed: 0
      };
    });
    setProfileStats(stats);
  };
  
  const handleProfileSelect = (profile) => {
    currentProfileRef.current = profile?.id;
    setSelectedProfile(profile);
    setSelectedView(profile ? 'profile' : 'home');
  };

  // This function is now less critical for refreshing stats, but can be kept for other purposes
  const handleRefreshData = () => {
    console.log("Real-time listener is active. Data will refresh automatically.");
  };

  const handleProfileEditorClose = (savedProfile) => {
    if (savedProfile) {
      // Profile was saved successfully, it will be updated via the real-time listener
      console.log('Profile saved:', savedProfile.id);
    }
    setProfileEditor(null);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    // A failed listener has stopped for good; Retry subscribes afresh.
    return (
      <Box sx={{ p: 3 }}>
        <LoadFailure thing="data profiles" onRetry={() => setListenerAttempt((n) => n + 1)} align="left" sx={{ p: 0 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      {/* Left Sidebar */}
      <Box sx={{ 
        width: 200, 
        flexShrink: 0,
        borderRight: '1px solid rgba(255, 255, 255, 0.03)',
        backgroundColor: 'background.paper',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <DataProfilesSidebar
          dataProfiles={dataProfiles}
          selectedProfile={selectedProfile}
          selectedView={selectedView}
          onProfileSelect={handleProfileSelect}
          onEditProfile={(p) => setProfileEditor({ mode: 'edit', profile: p })}
          onCopyProfile={(p) => setProfileEditor({ mode: 'copy', profile: p })}
          onDeleteProfile={setDeleteTarget}

          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          groupBy={groupBy}
          onGroupByChange={setGroupBy}
          profileStats={profileStats}
          attributeCategories={attributeCategories}
          onBack={onBack}
        />
      </Box>

      {/* Main Content */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
        <DataBreadcrumb
          tableName={table?.name}
          profileName={profileName(selectedProfile)}
          groupBy={groupBy}
          onManageFilesClick={() => handleProfileSelect(null)}
        />
        
        <Box sx={{ flexGrow: 1, overflow: 'auto', p: 3 }}>
          {selectedView === 'profile' && selectedProfile ? (
            <ProfileDashboard
              key={`${companyId}/${tableId}/${selectedProfile.id}`}
              profile={selectedProfile}
              stats={profileStats[selectedProfile.id] || {}}
              companyId={companyId}
              tableId={tableId}
              table={table}
              blockedLandingBatchId={blockedLandingBatchId}
              onFileUpload={handleRefreshData}
            />
          ) : (
            <FilesHome
              dataProfiles={dataProfiles}
              companyId={companyId}
              tableId={tableId}
              table={table}
              onProfileSelect={handleProfileSelect}
              profileStats={profileStats}
              blockedLandingBatchId={blockedLandingBatchId}
            />
          )}
        </Box>
      </Box>

      {/* Data Profile Editor Dialog — create, edit, or copy an existing profile */}
      {profileEditor && (
        <DataProfileEditor
          open={true}
          onClose={handleProfileEditorClose}
          table={table}
          initialProfile={profileEditor.mode === 'edit' ? profileEditor.profile : null}
          copySource={profileEditor.mode === 'copy' ? profileEditor.profile : null}
          companyId={companyId}
          tableId={tableId}
        />
      )}

      <DeleteDataProfileDialog
        open={Boolean(deleteTarget)}
        profile={deleteTarget}
        companyId={companyId}
        tableId={tableId}
        onClose={() => setDeleteTarget(null)}
      />
    </Box>
  );
}

export default DataCommandCenter;